import PropTypes from 'prop-types';
import HorizontalStackedBarChart from '@/components/charts/HorizontalStackedBarChart';
import sortBy from 'just-sort-by';
import { CHART_COLORS } from '@/consts';
import { numberFormat } from '@/utils/unitUtils';
import Decimal from 'decimal.js';
import { calculatePercentage, smartRound } from '@/utils/miscUtils';
import DoughnutChart from './DoughnutChart';

const colors = ['#BFEEDD', '#D4E1FD', '#ABC4FB', ...CHART_COLORS];

const CostBreakdownChart = ({
  breakdownResults = [],
  yAxisLabel,
  unit,
  digits = 2,
  variant = 'bar',
  itemLabel = 'Feedstock Item',
}) => {
  const amounts = breakdownResults.map(item => item.amount);
  const total = amounts.reduce((a, b) => a.plus(b), new Decimal(0));
  const results = breakdownResults.map(item => ({
    ...item,
    // Share of the gross (absolute) total, used to size the chart segments
    share: calculatePercentage(item.amount, amounts),
    // Signed share of the net total, so with negative amounts the positive percentages add up to more than 100%
    percentage: total.isZero() ? null : item.amount.div(total).times(100).toNumber(),
  }));
  const byShare = sortBy(results, 'share').reverse();
  const rows = byShare.map(item => item.share);
  const labels = byShare.map(item => item.label);
  const percentages = byShare.map(item => item.percentage);

  return (
    <div className="grid grid-cols-2 gap-8">
      {breakdownResults &&
        (variant === 'doughnut' ? (
          <div className="max-h-48">
            {/* Chart.js sizes doughnut arcs by absolute value, so the signed percentages can be plotted directly */}
            <DoughnutChart
              data={{ data: total.isZero() ? rows : percentages, labels, maxScope: total, colors, yAxisLabel }}
              variant="breakdown"
            />
          </div>
        ) : (
          <HorizontalStackedBarChart
            rows={rows}
            labels={labels}
            percentages={percentages}
            colors={colors}
            yAxisLabel={yAxisLabel}
          />
        ))}
      <div className="prose min-w-full">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-black leading-loose text-left">
              <th></th>
              <th>{itemLabel}</th>
              <th>Percentage</th>
              <th className="text-right">{unit}</th>
            </tr>
          </thead>
          <tbody>
            {byShare.map(({ label, amount, percentage }, index) => (
              <tr key={`${label}-${index}`} className="leading-loose">
                <td style={{ backgroundColor: colors[index] }} className="ml-2 w-2"></td>
                <td className="pl-2">{label}</td>
                <td>{percentage === null ? '—' : `${smartRound(percentage)}%`}</td>
                <td className="text-right">{amount && numberFormat(amount, digits)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="font-bold">
            <tr className="text-left">
              <td></td>
              <td>Total</td>
              <td>{total.isZero() ? '—' : '100%'}</td>
              <td className="text-right">{numberFormat(total, digits)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

CostBreakdownChart.propTypes = {
  breakdownResults: PropTypes.array.isRequired,
  yAxisLabel: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  digits: PropTypes.number,
  variant: PropTypes.oneOf(['doughnut', 'bar']),
  itemLabel: PropTypes.string,
};

export default CostBreakdownChart;
